import { CATALOG_BASE_URL } from '../constants';
import { FileResult, getJson, postForFile, postJson } from './files';

export { downloadBlob, getJson, postForFile } from './files';
export type { FileResult };

// ── Supplier reconciliation ──────────────────────────────────────────────────

export interface StatementCheck {
  name: string;
  readable: boolean;
  rows: number | null;
  reason: string;
}

export interface StatementTemplateInfo {
  columns: string[];
  example: string[][];
  rules: string[];
}

export interface SupplierReconSummary {
  period: string;
  messages: { level: 'success' | 'warning'; text: string }[];
  log: string[];
}

export const supplierTemplateUrl = `${CATALOG_BASE_URL}/supplier-recon/template`;

export function fetchStatementTemplateInfo() {
  return getJson<StatementTemplateInfo>('/supplier-recon/template-info', 'Could not load the template details');
}

export async function checkStatements(files: File[]): Promise<StatementCheck[]> {
  const form = new FormData();
  files.forEach((f) => form.append('statement_files', f));
  const data = await postJson<{ files: StatementCheck[] }>('/supplier-recon/check-statements', form, 'Could not check the statements');
  return data.files;
}

export function reconcileSupplier(ledger: File, statements: File[], period: { start: string; end: string } | null) {
  const form = new FormData();
  form.append('ledger_file', ledger);
  statements.forEach((f) => form.append('statement_files', f));
  form.append('options', JSON.stringify(period ? { start: period.start, end: period.end } : {}));
  return postForFile<SupplierReconSummary>('/supplier-recon/reconcile', form, 'supplier_reconciliation.xlsx', 'Reconciliation failed');
}

// ── Data anonymizer ──────────────────────────────────────────────────────────

export interface AnonymizerOptions {
  patterns: { key: string; label: string }[];
  locales: string[];
  default_name_columns: string[];
}

export interface TableInspection {
  rows: number;
  columns: string[];
  detected_columns: string[];
  patterns: Record<string, { label: string; columns: string[]; count: number; samples: string[] }>;
}

export interface AnonymizeSummary {
  rows?: number;
  columns?: string[];
  names_total: number;
  names_changed: number;
  pattern_tokens: number;
  replacements?: number;
  files: string[];
}

export interface CommonAnonymizeSettings {
  excludeNames: string[];
  locale: string;
  seed: number;
  enabledPatterns: string[];
}

export function fetchAnonymizerOptions() {
  return getJson<AnonymizerOptions>('/data-anonymizer/options', 'Could not load the anonymizer options');
}

export function inspectTable(file: File, nameColumns: string[]) {
  const form = new FormData();
  form.append('input_file', file);
  form.append('options', JSON.stringify({ name_columns: nameColumns }));
  return postJson<TableInspection>('/data-anonymizer/inspect', form, 'Could not read the file');
}

export function anonymizeTable(file: File, settings: CommonAnonymizeSettings & { nameColumns: string[]; selectedColumns: string[] }) {
  const form = new FormData();
  form.append('input_file', file);
  form.append(
    'options',
    JSON.stringify({
      name_columns: settings.nameColumns,
      selected_columns: settings.selectedColumns,
      exclude_names: settings.excludeNames,
      locale: settings.locale,
      seed: settings.seed,
      enabled_patterns: settings.enabledPatterns,
    })
  );
  return postForFile<AnonymizeSummary>('/data-anonymizer/anonymize', form, 'anonymized_files.zip', 'Anonymization failed');
}

export function anonymizePdf(
  file: File,
  settings: CommonAnonymizeSettings & { names: string[]; columnTerms: string[]; columnBandWidth: number }
) {
  const form = new FormData();
  form.append('input_file', file);
  form.append(
    'options',
    JSON.stringify({
      names: settings.names,
      column_terms: settings.columnTerms,
      column_band_width: settings.columnBandWidth,
      exclude_names: settings.excludeNames,
      locale: settings.locale,
      seed: settings.seed,
      enabled_patterns: settings.enabledPatterns,
    })
  );
  return postForFile<AnonymizeSummary>('/data-anonymizer/anonymize-pdf', form, 'anonymized_pdf.zip', 'PDF anonymization failed');
}

export function deanonymize(dataFile: File, mappingFile: File, patternMappingFile: File | null) {
  const form = new FormData();
  form.append('data_file', dataFile);
  form.append('mapping_file', mappingFile);
  if (patternMappingFile) form.append('pattern_mapping_file', patternMappingFile);
  return postForFile<{ names: number; pattern_tokens: number }>('/data-anonymizer/deanonymize', form, 'restored_file', 'Deanonymization failed');
}

// ── SME daily tracker ────────────────────────────────────────────────────────

export interface SmeTrackerSummary {
  mode: 'single' | 'merge' | 'batch';
  file?: string;
  output?: string;
  sheets_processed?: number;
  total_sheets?: number;
  input_files?: number;
  total_clients?: number;
  date_range?: number;
  teams?: Record<string, string>;
  files?: { file: string; output: string; sheets_processed: number; total_sheets: number }[];
}

export function convertTrackers(files: File[], mode: 'merge' | 'batch') {
  const form = new FormData();
  files.forEach((f) => form.append('input_files', f));
  form.append('options', JSON.stringify({ mode }));
  return postForFile<SmeTrackerSummary>('/sme-tracker/convert', form, 'converted_daily_tracker.xlsx', 'Conversion failed');
}
